/**
 * Utility functions for map-related operations
 */

/**
 * Formats an address object or string into a single address string
 * @param {Object|string} address - Address object or string
 * @returns {string} Formatted address string
 */
export const formatAddress = (address) => {
  if (!address) return '';
  
  if (typeof address === 'string') {
    return address;
  }
  
  if (typeof address === 'object') {
    const addressParts = [
      address.address_line1,
      address.address_line2,
      address.city,
      address.state,
      address.zip_code
    ].filter(Boolean);
    
    return addressParts.join(', ');
  }
  
  return '';
};

/**
 * Extracts and formats address from appointment data
 * @param {Object} appointment - Appointment object
 * @returns {string} Formatted address string
 */
export const getAppointmentAddress = (appointment) => {
  if (!appointment) return '';
  
  // Try to get address from job report first
  const reportAddress = appointment.job?.report?.address;
  if (reportAddress) {
    return formatAddress(reportAddress);
  }
  
  // Fallback to job address
  const jobAddress = appointment.job?.address;
  if (jobAddress) {
    return formatAddress(jobAddress);
  }
  
  // Fallback to direct appointment fields (if any)
  const directAddress = {
    address_line1: appointment.address_line1,
    address_line2: appointment.address_line2,
    city: appointment.city,
    state: appointment.state,
    zip_code: appointment.zip_code
  };
  
  return formatAddress(directAddress);
};

/**
 * Opens Google Maps in a new tab with the specified address
 * @param {string} address - Address to search for
 */
export const openGoogleMaps = (address) => {
  if (!address || !address.trim()) {
    console.warn('No address provided for Google Maps navigation');
    return;
  }
  
  const encodedAddress = encodeURIComponent(address.trim());
  const mapsUrl = `https://maps.google.com/maps?q=${encodedAddress}`;
  
  window.open(mapsUrl, '_blank', 'noopener,noreferrer');
};

/**
 * Opens Google Maps for an appointment's address
 * @param {Object} appointment - Appointment object
 */
export const navigateToAppointment = (appointment) => {
  const address = getAppointmentAddress(appointment);
  
  if (!address) {
    console.warn('No address found for appointment:', appointment);
    return;
  }
  
  openGoogleMaps(address);
};

/**
 * Maps backend house type values to display names
 * @param {string} houseType - Backend house type value
 * @returns {string} Formatted house type display name
 */
export const formatHouseType = (houseType) => {
  if (!houseType) return "";

  const houseTypeMap = {
    SINGLE_FAMILY: "Single Family",
    TOWNHOUSE: "Townhouse",
    MULTI_FAMILY: "Multi-Family",
    CONDO: "Condo",
    LOT: "Lot",
    APARTMENT: "Apartment",
    MANUFACTURED: "Manufactured",
  };

  return houseTypeMap[houseType] || houseType;
};
